import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  loadConsent,
  saveConsent,
  detectJurisdiction,
  getDefaultsForJurisdiction,
  getCookieConsentStrings,
  type CookieConsentPreferences,
  type ConsentJurisdiction,
} from "@/lib/cookie-consent";

// ---------------------------------------------------------------------------
// Rewardful marketing script initialisation (gated on marketing consent)
// ---------------------------------------------------------------------------
function initMarketingScripts() {
  try {
    // Rewardful uses a lazy-init stub; call ready() to activate tracking
    if (typeof window.rewardful === "function") {
      window.rewardful("ready", () => {/* tracking active */});
    }
  } catch {
    // ignore if script not loaded
  }
}

declare global {
  interface Window {
    rewardful?: (...args: unknown[]) => void;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CookieConsent() {
  const { user } = useAuth();
  const lang = useLanguage();
  const t = getCookieConsentStrings(lang);

  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [jurisdiction, setJurisdiction] = useState<ConsentJurisdiction>("EU");
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Detect jurisdiction and set defaults, then show banner if no prior consent.
  // Defer everything to idle time so the cookie banner does NOT become the LCP element.
  useEffect(() => {
    const existing = loadConsent();
    if (existing) {
      // Consent already given – apply scripts if marketing allowed
      if (existing.marketing) initMarketingScripts();
      return;
    }

    const scheduleIdle: (cb: () => void) => void =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (cb) =>
            (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
              .requestIdleCallback(cb, { timeout: 2500 })
        : (cb) => setTimeout(cb, 1500);

    scheduleIdle(() => {
      detectJurisdiction().then((j) => {
        setJurisdiction(j);
        const defaults = getDefaultsForJurisdiction(j);
        setAnalytics(defaults.analytics);
        setMarketing(defaults.marketing);
        setVisible(true);
      });
    });
  }, []);

  // Listen for consent updates from other tabs / components
  useEffect(() => {
    const handler = (e: Event) => {
      const prefs = (e as CustomEvent<CookieConsentPreferences>).detail;
      if (prefs.marketing) initMarketingScripts();
    };
    window.addEventListener("cookieConsentUpdated", handler);
    return () => window.removeEventListener("cookieConsentUpdated", handler);
  }, []);

  const persist = useCallback(
    (prefs: CookieConsentPreferences) => {
      saveConsent(prefs);
      if (prefs.marketing) initMarketingScripts();
      // Sync to Supabase user profile (fire-and-forget)
      if (user) {
        supabase
          .from("profiles")
          .update({
            cookie_consent: {
              analytics: prefs.analytics,
              marketing: prefs.marketing,
              jurisdiction: prefs.jurisdiction,
              consentedAt: prefs.consentedAt,
            },
          })
          .eq("user_id", user.id)
          .then(() => {/* best-effort */});
      }
      setVisible(false);
      setShowPreferences(false);
    },
    [user],
  );

  const buildPrefs = useCallback(
    (overrides: Partial<Pick<CookieConsentPreferences, "analytics" | "marketing">>): CookieConsentPreferences => ({
      essential: true,
      analytics: overrides.analytics ?? analytics,
      marketing: overrides.marketing ?? marketing,
      jurisdiction,
      version: 1,
      consentedAt: new Date().toISOString(),
    }),
    [analytics, marketing, jurisdiction],
  );

  const handleAcceptAll = () => persist(buildPrefs({ analytics: true, marketing: true }));
  const handleRejectAll = () => persist(buildPrefs({ analytics: false, marketing: false }));
  const handleSavePreferences = () => persist(buildPrefs({}));

  if (!visible) return null;

  const isCA = jurisdiction === "US_CA";

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Main banner — compact on mobile to avoid covering hero               */}
      {/* ------------------------------------------------------------------ */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-card p-3 shadow-lg md:bottom-4 md:left-auto md:right-4 md:max-w-sm md:rounded-xl md:border md:p-4">
        <p className="mb-1 text-xs font-medium text-foreground md:text-sm">{t.title}</p>
        <p className="mb-2 line-clamp-2 text-[11px] text-muted-foreground md:mb-3 md:line-clamp-none md:text-xs">
          {isCA ? t.descriptionCA : t.description}{" "}
          <Link to="/privacy" className="text-primary underline">
            {t.privacyLink}
          </Link>
        </p>
        <div className="flex flex-col gap-1.5 md:gap-2">
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAcceptAll} className="h-8 flex-1 text-xs md:h-9 md:text-sm">
              {t.acceptAll}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleRejectAll}
              className="h-8 flex-1 text-xs md:h-9 md:text-sm"
            >
              {t.rejectAll}
            </Button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-full text-[11px] md:h-8 md:text-xs"
            onClick={() => setShowPreferences(true)}
          >
            {isCA ? t.doNotSell : t.managePreferences}
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Preferences sheet                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Sheet open={showPreferences} onOpenChange={setShowPreferences}>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-xl">
          <SheetHeader className="mb-4">
            <SheetTitle>{t.managePreferences}</SheetTitle>
          </SheetHeader>

          <div className="space-y-4">
            {/* Essential – always on */}
            <CategoryRow
              title={t.categories.essential.title}
              description={t.categories.essential.description}
              checked={true}
              disabled={true}
              label={t.alwaysOn}
              onCheckedChange={() => {/* locked */}}
            />

            {/* Analytics */}
            <CategoryRow
              title={t.categories.analytics.title}
              description={t.categories.analytics.description}
              checked={analytics}
              onCheckedChange={setAnalytics}
            />

            {/* Marketing */}
            <CategoryRow
              title={t.categories.marketing.title}
              description={t.categories.marketing.description}
              checked={marketing}
              onCheckedChange={setMarketing}
            />
          </div>

          <div className="mt-6 flex gap-2">
            <Button className="flex-1" onClick={handleSavePreferences}>
              {t.savePreferences}
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleRejectAll}>
              {t.rejectAll}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helper sub-component
// ---------------------------------------------------------------------------

interface CategoryRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  label?: string;
  onCheckedChange: (v: boolean) => void;
}

function CategoryRow({ title, description, checked, disabled, label, onCheckedChange }: CategoryRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border pb-4 last:border-0">
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-col items-center gap-1 pt-0.5">
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          aria-label={title}
        />
        {label && <span className="text-[10px] text-muted-foreground">{label}</span>}
      </div>
    </div>
  );
}
